import classNames from 'classnames'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { partnerDataStoreService } from 'legacy/lut/classes/services/PartnerDataStoreService'
import { PartnerLogButton } from 'legacy/lut/components/widgets/public/PartnerLogButton'
import React, { useEffect, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { Loader } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import { PartnerThirdPartyLogButton } from 'onboarding/components/widgets/public/thirdPartyLog/PartnerThirdPartyLogButton'
import settings from 'src/config/settings'

import styles from './Expanded.module.scss'

export interface IProps {
  partnerId: string
  partner: DataModel
  openPartnerDialog: (item: DataModel, tab?: number) => void
}

const removeLastComma = (str: string = '') =>
  str[str.length - 1] === ',' ? str.substring(0, str.length - 1) : str

export const Expanded = (props: IProps) => {
  const { partner, partnerId } = props

  const [programCodes, setProgramCodes] = useState<any[]>([])
  const [accreditationCodes, setAccreditationCodes] = useState<any[]>([])
  const [isLoading, setLoading] = useState(true)
  const [progFilter, setProgFilter] = useState('')
  const [accFilter, setAccFilter] = useState('')
  const [showAllProg, setShowAllProg] = useState(false)
  const [showAllAcc, setShowAllAcc] = useState(false)

  useEffect(() => {
    setLoading(true)
    getAllCodes(partnerId)
      .then((result) => {
        setProgramCodes(result.ProgramCodes)
        setAccreditationCodes(result.AccreditationCodes)
        setAccFilter('')
        setProgFilter('')
        setShowAllAcc(false)
        setShowAllProg(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [partnerId])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className={styles.root}>
      {partner.get('Modified') && (
        <p className={styles.date}>
          <span>Updated Date:</span>
          <span>{partner.get('Modified')}</span>
        </p>
      )}
      <div className={styles.row}>
        <div className={styles.col}>
          <p className={styles.title}>Physical</p>
          <div className={styles.data}>
            <p className={styles.data__label}>Name:</p>
            <p className={styles.data__value}>{partner.get('CommunicationName')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Address:</p>
            <p className={styles.data__value}>
              <span>{partner.get('AddressLine1')}</span>{' '}
              <span>
                {partner.get('AddressLine3')
                  ? partner.get('AddressLine2')
                  : removeLastComma(partner.get('AddressLine2'))}
              </span>{' '}
              <span>{removeLastComma(partner.get('AddressLine3'))}</span>
            </p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>City:</p>
            <p className={styles.data__value}>{partner.get('City')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>State:</p>
            <p className={styles.data__value}>{partner.get('State')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Postal:</p>
            <p className={styles.data__value}>{partner.get('ZIPCode')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Country:</p>
            <p className={styles.data__value}>{partner.get('Country')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Location ID:</p>
            <p className={styles.data__value}>{partner.get('LocationID')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>RPL Status:</p>
            <p className={styles.data__value}>{partner.get('RPL_STATUS')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Stage:</p>
            <p className={styles.data__value}>{partner.get('STAGE')}</p>
          </div>
        </div>

        <div className={styles.col}>
          <p className={styles.title}>Legal</p>
          <div className={styles.data}>
            <p className={styles.data__label}>Name:</p>
            <p className={styles.data__value}>{partner.get('CompanyLegalName')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Address:</p>
            <p className={styles.data__value}>
              <span>{partner.get('LegalAddr1')}</span>{' '}
              <span>
                {partner.get('LegalAddr3')
                  ? partner.get('LegalAddr2')
                  : removeLastComma(partner.get('LegalAddr2'))}
              </span>{' '}
              <span>{removeLastComma(partner.get('LegalAddr3'))}</span>
            </p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>City:</p>
            <p className={styles.data__value}>{partner.get('LegalCity')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>State:</p>
            <p className={styles.data__value}>{partner.get('LegalState')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Postal:</p>
            <p className={styles.data__value}>{partner.get('LegalPostalCode')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Country:</p>
            <p className={styles.data__value}>{partner.get('LegalCountry')}</p>
          </div>
        </div>

        <div className={styles.col}>
          <p className={styles.title}>Partner Contact</p>
          <div className={styles.data}>
            <p className={styles.data__label}>Main Phone:</p>
            <p className={styles.data__value}>{partner.get('MainPhone')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Email:</p>
            <p className={styles.data__value}>{partner.get('Email')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>LinkedIn:</p>
            <p className={styles.data__value}>{partner.get('LinkedInLink')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Facebook:</p>
            <p className={styles.data__value}>{partner.get('FacebookLink')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>CorporateURL:</p>
            <p className={styles.data__value}>{partner.get('URL')}</p>
          </div>
          <br />
          <p className={styles.title}>Bank</p>
          <div className={styles.data}>
            <p className={styles.data__label}>Account:</p>
            <p className={styles.data__value}>{partner.get('BankAccount')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Name:</p>
            <p className={styles.data__value}>{partner.get('BankName')}</p>
          </div>
          <div className={styles.data}>
            <p className={styles.data__label}>Account Name:</p>
            <p className={styles.data__value}>{partner.get('BankAccountName')}</p>
          </div>
        </div>

        <div className={styles.col}>
          <p className={styles.title}>Program Codes</p>
          <Input
            classes={{
              root: styles.input,
            }}
            placeholder={'Search...'}
            value={progFilter}
            onChange={(value) => setProgFilter(value)}
          />
          {getParsedData(programCodes, progFilter, showAllProg).map(
            (
              { CodeName, START_DATE, END_DATE, Code, ApolloCode, Status, Action, Modified }: any,
              index: any
            ) => (
              <div key={index} className={styles.requests}>
                {getRequestsIcon(Status, Modified)}
                <Tooltip
                  tipContent={() => (
                    <div>
                      {Action === 'Modify' ? (
                        <>
                          Modified "date" <br />
                        </>
                      ) : Action === 'Add' ? (
                        <>
                          Added "date" <br />
                        </>
                      ) : null}
                      Code: {Code} <br />
                      {ApolloCode ? (
                        <>
                          Extended: {ApolloCode} <br />
                        </>
                      ) : null}
                      Start: {START_DATE} <br />
                      End: {END_DATE} <br />
                    </div>
                  )}
                >
                  <div className={getClassRequests(Status, Modified)}>
                    {CodeName || Code}
                    {getIconByDate(END_DATE || '')}
                  </div>
                </Tooltip>
              </div>
            )
          )}
          {!showAllProg && programCodes.length > 5 ? (
            <div className={styles.moreBtn} onClick={() => setShowAllProg(true)}>
              More
            </div>
          ) : null}
          {!programCodes.length && <div>Not found</div>}
          {settings.appIdentifier.id === 'hpscoutso' ? (
            <>
              <hr />
              <Button onClick={() => props.openPartnerDialog(partner, 1)}>Edit</Button>
            </>
          ) : null}
        </div>
        <div className={styles.col}>
          <p className={styles.title}>Accreditation Codes</p>
          <Input
            classes={{
              root: styles.input,
            }}
            placeholder={'Search...'}
            value={accFilter}
            onChange={(value) => setAccFilter(value)}
          />
          {getParsedData(accreditationCodes, accFilter, showAllAcc).map(
            (
              { Code, CodeName, START_DATE, END_DATE, Status, Action, Modified }: any,
              index: any
            ) => (
              <div key={index} className={styles.requests}>
                {getRequestsIcon(Status, Modified)}
                <Tooltip
                  tipContent={() => (
                    <div>
                      {Action === 'Modify' ? (
                        <>
                          Modified "date" <br />
                        </>
                      ) : Action === 'Add' ? (
                        <>
                          Added "date" <br />
                        </>
                      ) : null}
                      Code: {Code} <br />
                      Start: {START_DATE} <br />
                      End: {END_DATE || 'unknown'}
                    </div>
                  )}
                >
                  <div className={getClassRequests(Status, Modified)}>
                    {CodeName || Code}
                    {getIconByDate(END_DATE || '')}
                  </div>
                </Tooltip>
              </div>
            )
          )}
          {!showAllAcc && accreditationCodes.length > 5 ? (
            <div className={styles.moreBtn} onClick={() => setShowAllAcc(true)}>
              More
            </div>
          ) : null}
          {!accreditationCodes.length && <div>Not found</div>}
          {settings.appIdentifier.id === 'hpscoutso' ? (
            <>
              <hr />
              <Button onClick={() => props.openPartnerDialog(partner, 2)}>Edit</Button>
            </>
          ) : null}
        </div>
      </div>
      <div className={styles.logButtons}>
        <PartnerLogButton partnerID={partnerId} caption={'Audit trail'} />
        <PartnerThirdPartyLogButton partnerId={partnerId} caption={'Third party logs'} />
      </div>
    </div>
  )
}

const getRequestsIcon = (status: string, Modified: string) => {
  switch (status) {
    case 'WIP':
      return (
        <i
          className={classNames(
            styles.requests__icon,
            styles['requests--wip'],
            'kbase kbase-lvl-clock'
          )}
        />
      )
    case 'Rejected':
      return (
        <i
          className={classNames(
            styles.requests__icon,
            styles['requests--rejected'],
            'kbase kbase-info-alt'
          )}
        />
      )
    case 'Done':
      return (
        Modified &&
        Date.parse(Modified) > +new Date() - 86400000 && (
          <i
            className={classNames(
              styles.requests__icon,
              styles['requests--done'],
              'kbase kbase-check-circle'
            )}
          />
        )
      )
    case 'New':
      return (
        <i
          className={classNames(styles.requests__icon, styles['requests--new'], 'kbase kbase-add')}
        />
      )
  }
  return
}

const getAllCodes = async (partnerId: string) => {
  let ProgramCodes = []
  let AccreditationCodes = []

  if (partnerId) {
    const resultPromise = await partnerDataStoreService.getCodes([partnerId], 200)

    const crudAccreditation = CrudStore.get(
      CrudStoreItem.PartnerFlagsRequests,
      `getAllCodes-Accreditaion-${partnerId}`
    )
    crudAccreditation.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'PartnerId',
          operator: Operator.EQUAL,
          value: partnerId,
        },
        {
          field: 'Type',
          operator: Operator.EQUAL,
          value: 'Accreditation',
        },
        {
          operator: Operator.NOTEQUAL,
          field: 'Status',
          value: 'Done',
        },
      ],
    })
    crudAccreditation.setPaging({
      page: 0,
      pageSize: 100,
    })
    const accreditationPromise = crudAccreditation.fetch()

    const crudProgram = CrudStore.get(
      CrudStoreItem.PartnerFlagsRequests,
      `getAllCodes-Program-${partnerId}`
    )
    crudProgram.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'PartnerId',
          operator: Operator.EQUAL,
          value: partnerId,
        },
        {
          field: 'Type',
          operator: Operator.EQUAL,
          value: 'Program',
        },
        {
          operator: Operator.NOTEQUAL,
          field: 'Status',
          value: 'Done',
        },
      ],
    })
    crudProgram.setPaging({
      page: 0,
      pageSize: 100,
    })
    const programPromise = crudProgram.fetch()

    const [result, accreditation, program] = await Promise.all([
      resultPromise,
      accreditationPromise,
      programPromise,
    ])

    const accreditationCodesRequests = accreditation.items
    const programCodesRequests = program.items

    if (accreditationCodesRequests.length && result) {
      let newAccreditationCodes = result.AccreditationCodes.items.slice()
      accreditationCodesRequests.forEach((item) => {
        const el = newAccreditationCodes.find((v) => v.Code === item.Code)
        newAccreditationCodes = newAccreditationCodes.filter((v) => v.Code !== item.Code)
        newAccreditationCodes.push({
          ...item,
          ...el,
          END_DATE: item.EndDate,
          START_DATE: el ? el.START_DATE : item.StartDate,
          PARTNER_ID: item.PartnerId,
        })
      })
      result.AccreditationCodes.items = newAccreditationCodes
    }

    if (programCodesRequests.length && result) {
      let newProgramCodes = result.ProgramCodes.items.slice()
      programCodesRequests.forEach((item) => {
        const el = newProgramCodes.find((v) => v.Code === item.Code)
        newProgramCodes = newProgramCodes.filter((v) => v.Code !== item.Code)
        newProgramCodes.push({
          ...item,
          ...el,
          END_DATE: item.EndDate,
          START_DATE: el ? el.START_DATE : item.StartDate,
          PARTNER_ID: item.PartnerId,
        })
      })
      result.ProgramCodes.items = newProgramCodes
    }

    if (result) {
      ProgramCodes = result.ProgramCodes.items
      AccreditationCodes = result.AccreditationCodes.items
    }
  }
  return {
    ProgramCodes,
    AccreditationCodes,
  }
}

const getParsedData = (codes: any[], valueFilter: string, showAll: boolean) => {
  const filter = valueFilter.toUpperCase()
  let result = codes

  if (filter) {
    result = codes.filter(
      (v) =>
        (v.CodeName && ~v.CodeName.toUpperCase().indexOf(filter)) ||
        (v.Code && ~v.Code.toUpperCase().indexOf(filter))
    )
  }
  return result.slice(0, !showAll ? 5 : undefined)
}

const getClassRequests = (status: string, Modified: string) => {
  let styleStatus = ''
  if (status === 'WIP') {
    styleStatus = styles['requests--wip']
  } else if (status === 'Done' && Modified && Date.parse(Modified) > +new Date() - 86400000) {
    styleStatus = styles['requests--done']
  } else if (status === 'Rejected') {
    styleStatus = styles['requests--rejected']
  } else if (status === 'New') {
    styleStatus = styles['requests--new']
  }

  return styleStatus
}

const getIconByDate = (date: string) => {
  let convertedDate
  const dateByDash = date.split('-')
  const now = new Date()
  if (dateByDash.length === 3) {
    convertedDate = new Date(date)
  } else {
    date = date.replace(/  +/g, ' ').replace('12:00AM', '').replace('PM', '').replace('AM', '')
    const dateBySpace = date.split(' ')
    convertedDate = new Date(
      `${dateBySpace[1]}-${dateBySpace[0]}-${dateBySpace[2]} ${dateBySpace[3] || ''}`
    )
  }
  if (convertedDate.toString() === 'Invalid Date') {
    return <span className={styles.unknowndate}>?</span>
  } else if (convertedDate < now) {
    return <span className={styles.errordate}>!</span>
  }
  return ''
}
